<template>
  <div class="w-full bg-slate-50 min-h-screen p-4" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-5xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6 flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-slate-700">Tipos de Combustível</h1>
          <p class="text-sm text-slate-500">Gerencie as opções de combustível da frota</p>
        </div>
      </div>

      <!-- FORMULÁRIO DE CADASTRO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-gas-station</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                  {{ fuelData.fuel_id ? 'EDITAR ITEM' : 'NOVO ITEM' }}
              </h2>
          </div>
          
          <div class="p-8">
              <v-form @submit.prevent="createOrUpdate">
                <div class="flex flex-col md:flex-row md:items-center gap-3">
                  <div class="flex-1 min-w-0">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Nome do Combustível</label>
                    <v-text-field
                      v-model="fuelData.fuel_desc"
                      ref="fuelName"
                      placeholder="Ex: Diesel, Gasolina, Etanol..."
                      outlined
                      dense
                      hide-details
                      class="custom-input"
                    />
                  </div>
                  
                  <div class="flex gap-2 flex-none pt-6">
                    <button 
                      type="submit"
                      class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[10px] px-4 py-2.5 rounded shadow transition-all uppercase flex items-center justify-center gap-2 whitespace-nowrap h-[40px]"
                    >
                      <v-icon small color="white">mdi-check</v-icon>
                      {{ fuelData.fuel_id ? 'EDITAR' : 'SALVAR' }}
                    </button>
                    <button 
                      v-if="fuelData.fuel_id"
                      type="button"
                      @click="newData"
                      class="bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-[10px] px-4 py-2.5 rounded transition-all uppercase whitespace-nowrap h-[40px]"
                    >
                      LIMPAR
                    </button>
                  </div>
                </div>
                <p v-if="fuelData.fuel_id" class="mt-4 text-[10px] text-orange-500 font-medium italic">
                  * Você está editando um item. Para adicionar um novo, clique em "LIMPAR".
                </p>
              </v-form>
          </div>
      </section>

      <!-- GRID DE DADOS -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <div class="flex items-center gap-3">
                <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ITENS CADASTRADOS</h2>
              </div>
              
              <div class="flex items-center gap-4 flex-1 justify-end">
                <div class="relative w-80">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
                  </div>
                  <input 
                    v-model="search"
                    placeholder="PESQUISAR..."
                    class="bg-white/10 border border-white/20 rounded-lg py-2 pl-10 pr-4 text-[11px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                  >
                </div>
              </div>
          </div>

          <div class="p-0">
              <v-data-table
                :headers="headers"
                :items="data"
                :search="search"
                :items-per-page="10"
                :loading="loader"
                loading-text="CARREGANDO..."
                class="custom-table"
                hide-default-footer
              >
                <template v-slot:item.fuel_desc="{ item }">
                  <span class="text-sm font-medium text-slate-700 uppercase">{{ item.fuel_desc }}</span>
                </template>

                <template v-slot:item.action="{ item }">
                  <div class="flex justify-end gap-2">
                    <button @click="loadData(item)" class="p-2 hover:bg-blue-50 text-blue-500 rounded-lg transition-colors">
                      <v-icon small>mdi-pencil</v-icon>
                    </button>
                    <button @click="deleteItem(item.fuel_id)" class="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors">
                      <v-icon small>mdi-delete</v-icon>
                    </button>
                  </div>
                </template>
              </v-data-table>
          </div>
      </section>
    </div>

    <!-- Feedback -->
    <v-snackbar multi-line :color="snack.color" v-model="snack.show" transition="fade-transition" elevation="24">
      <div class="flex items-center gap-3">
        <v-icon color="white">{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="font-bold text-xs uppercase tracking-wider">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data() {
    return {
      search: "",
      loader: true,
      showSearch: false,
      data: [],
      fuelData: {
        fuel_desc: "",
        fuel_id: null
      },
      snack: {
        show: false,
        message: "",
        color: ""
      },
      headers: [
        {
          text: "Nome",
          align: "start",
          sortable: false,
          value: "fuel_desc"
        },
        { text: "Ações", align: "right", value: "action", sortable: false }
      ]
    };
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.data = [];
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/settings/fuels`)
        .then(r => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch(e => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    newData() {
      this.fuelData.fuel_desc = "";
      this.fuelData.fuel_id = null;
      this.$refs.fuelName.focus();
    },
    loadData(data) {
      this.fuelData.fuel_desc = data.fuel_desc;
      this.fuelData.fuel_id = data.fuel_id;
    },
    createOrUpdate() {
      if (this.fuelData.fuel_id) {
        this.$http
          .put(`/settings/fuels/${this.fuelData.fuel_id}`, this.fuelData)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = true;
            this.initialize();
            this.$store.dispatch("setLoader", this.loader);
            this.newData();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$http
          .post(`/settings/fuels`, this.fuelData)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = true;
            this.initialize();
            this.$store.dispatch("setLoader", this.loader);
            this.newData();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/settings/fuels/${id}`)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.initialize();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    }
  }
};
</script>

<style scoped>
.min-h-screen { min-height: 100vh; }
.bg-slate-50 { background-color: #f8fafc; }
.w-full { width: 100%; }
.p-4 { padding: 1rem; }

/* Custom Table Override */
.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep thead { background-color: #f8fafc !important; }
.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 0.75rem !important; 
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 0.875rem !important;
}

/* Custom Input */
.custom-input ::v-deep .v-input__slot {
  background: white !important;
  border-radius: 8px !important;
  box-shadow: none !important;
}
.custom-input ::v-deep fieldset {
  border-color: #e2e8f0 !important;
}
.custom-input ::v-deep .v-label {
  font-size: 0.875rem !important;
  color: #64748b !important;
}
</style>